import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppForeground } from '@/hooks/useAppForeground';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DollarSign, Wallet, Building2, ArrowDownToLine, ArrowUpFromLine, Zap,
  ChevronLeft, Loader2, CheckCircle2, XCircle,
  Clock, CreditCard, Landmark, AlertTriangle, Plus,
  ShieldCheck, Lock, ArrowDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { cleanBankLabel } from '@/lib/walletActivity';
import { withdrawalAmountSchema } from '@/lib/validationSchemas';
import { useToast } from '@/hooks/use-toast';
import { WalletBonusesSection } from '@/components/nfl/bonuses/WalletBonusesSection';
import { BrandedPayButton } from '@/components/nfl/BrandedPayButton';
import { SoapCheckoutFrame } from '@/components/wallet/SoapCheckoutFrame';
import { mapDepositError } from '@/lib/depositErrors';
import { useLocanceGeoCheck, type GeoBlockReason } from '@/hooks/useLocanceGeoCheck';
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen';
import { pingBeforeMoneyActionIfStale } from '@/lib/devicePing';
import { eligibilityToGeoBlock } from '@/lib/geoEligibility';
import { GEO_ENABLED, GEO_OBSERVE } from '@/lib/locance';
import { RAIL_PAYPAL, RAIL_SOAP } from '@/lib/depositRails';
import { feeSentence } from '@/lib/depositFees';
import { PaymentMarks } from '@/components/PaymentMarks';
// SAB-573: single source of truth for Persona template + environment ids (env-driven).
import { PERSONA_TIER1_TEMPLATE_ID, PERSONA_TIER2_TEMPLATE_ID, PERSONA_ENVIRONMENT_ID, launchPersonaHosted } from '@/lib/personaKyc';

declare global {
  interface Window {
    paypal?: any;
  }
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PlayerWallet {
  id: string;
  balance: number;
  sabotage_credits: number;
  pending_deposits: number;
}

interface LinkedBank {
  id: string;
  bank_name: string;
  account_last_four: string | null;
  account_type: string | null;
  status: string;
}

interface WalletTransaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  created_at: string;
  completed_at: string | null;
  prize_pool_amount: number | null;
  rake_amount: number | null;
  description: string | null;
}

const CREDIT_AMOUNTS = [5, 10, 20, 50];
const FEE_RATE = 0.20;
// SAB-512 Phase 6: en-US money formatting (2dp + thousands separators) for the
// live deposit/withdraw summaries and CTA labels, per the design canvas.
const fmtUSD = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ------------------------------------------------------------------ */
/*  SAB-367 Persona KYC (tier-1) constants + lazy CDN SDK loader       */
/* ------------------------------------------------------------------ */

// SAB-573: the tier-1/tier-2 template ids + environment id are env-driven and
// live in personaKyc.ts (single source of truth — imported above). PROD supplies
// VITE_PERSONA_TIER1_TEMPLATE_ID / VITE_PERSONA_TIER2_TEMPLATE_ID /
// VITE_PERSONA_ENVIRONMENT_ID; previews/local fall back to sandbox automatically.
const PERSONA_SDK_SRC = 'https://cdn.withpersona.com/dist/persona-v5.5.0.js';
const PERSONA_SDK_INTEGRITY =
  'sha384-UK+a2yEU9KOzEmsgI4IlkrXWE4AekM/iAgWF60Zuyule702g7qaQ2nYccO3tnT0A';

// Module-scoped cache: a re-mount of the component must not re-inject.
let personaSdkPromise: Promise<void> | null = null;

function loadPersonaSdk(): Promise<void> {
  if ((window as any).Persona) return Promise.resolve();
  if (personaSdkPromise) return personaSdkPromise;

  personaSdkPromise = new Promise<void>((resolve, reject) => {
    // A previous mount may have inserted the tag without Persona being ready yet.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PERSONA_SDK_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => {
        personaSdkPromise = null;
        reject(new Error('Persona SDK failed to load'));
      });
      return;
    }
    const script = document.createElement('script');
    script.src = PERSONA_SDK_SRC;
    script.integrity = PERSONA_SDK_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      personaSdkPromise = null;
      reject(new Error('Persona SDK failed to load'));
    };
    document.head.appendChild(script);
  });

  return personaSdkPromise;
}

/* ------------------------------------------------------------------ */
/*  SAB-490 4b — branded KYC intro/gate + result states.               */
/*  Persona owns the license/selfie CAPTURE inside its own SDK modal;   */
/*  these are the branded intro + result screens around it. Money       */
/*  release stays server-authoritative (check_kyc_gate) — see           */
/*  recheckKycAndContinue; kycStatus here is DISPLAY-ONLY.              */
/* ------------------------------------------------------------------ */

function KycGate({ tier, verifying, onStart }: { tier: 1 | 2; verifying: boolean; onStart: () => void }) {
  const action = tier === 2 ? 'withdrawal' : 'deposit';
  return (
    <Card className="border-primary/20 bg-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-display font-bold text-foreground">Verify your identity</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Required by law before your first {action}. Your information is encrypted and never shared with other players.
            </p>
          </div>
        </div>
        <Button onClick={onStart} disabled={verifying} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
          {verifying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting…</> : <><ShieldCheck className="h-4 w-4 mr-2" /> Start verification</>}
        </Button>
        <p className="flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground">
          <Lock className="h-3 w-3" /> Verified by Persona · bank-level encryption · usually under 2 min
        </p>
      </CardContent>
    </Card>
  );
}

function KycResultModal({ result, tier, onRetry, onClose }: {
  result: 'verifying' | 'verified' | 'pending' | 'needs_review' | 'failed' | 'tier_required';
  tier: 1 | 2;
  onRetry: () => void;
  onClose: () => void;
}) {
  const action = tier === 2 ? 'withdrawal' : 'deposit';
  const content = {
    verifying:    { icon: <Loader2 className="h-7 w-7 text-primary animate-spin" />, title: 'Verifying your identity…', body: 'This usually takes under a minute. Hang tight.' },
    verified:     { icon: <CheckCircle2 className="h-7 w-7 text-success" />,          title: "You're verified!",        body: `Identity confirmed. Continuing to your ${action}.` },
    pending:      { icon: <Clock className="h-7 w-7 text-gold" />,                     title: "We're reviewing your documents", body: "This can take a few minutes. Your status updates automatically. You can close this and check back." },
    needs_review: { icon: <Clock className="h-7 w-7 text-gold" />,                     title: 'Your documents are in manual review', body: "A person is reviewing your submission. This can take up to 1–2 business days. We'll update your status once it's done." },
    failed:       { icon: <XCircle className="h-7 w-7 text-damage" />,                 title: "We couldn't verify your identity", body: "Something didn't match. You can try again, or contact support if this keeps happening." },
    tier_required:{ icon: <ShieldCheck className="h-7 w-7 text-gold" />,                title: 'Extra verification for withdrawals', body: "You're verified for deposits. Withdrawals need an additional ID + selfie check. It only takes a minute." },
  }[result];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <Card className="w-full max-w-[360px] border-border bg-card">
        <CardContent className="p-6 text-center space-y-3">
          <div className="h-14 w-14 rounded-full bg-muted border border-border flex items-center justify-center mx-auto">
            {content.icon}
          </div>
          <h3 className="text-lg font-display font-bold text-foreground">{content.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{content.body}</p>
          {result === 'failed' && (
            <div className="space-y-2 pt-1">
              <Button onClick={onRetry} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                <ShieldCheck className="h-4 w-4 mr-2" /> Try again
              </Button>
              <a href="mailto:support@sabotagefantasy.com" className="block text-xs text-primary hover:text-primary/80">Contact support</a>
            </div>
          )}
          {(result === 'pending' || result === 'needs_review') && (
            <Button onClick={onClose} variant="outline" className="w-full h-11 border-border text-foreground hover:bg-muted font-semibold">Done</Button>
          )}
          {result === 'tier_required' && (
            <div className="space-y-2 pt-1">
              <Button onClick={onRetry} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                <ShieldCheck className="h-4 w-4 mr-2" /> Verify for withdrawals
              </Button>
              <button onClick={onClose} className="block w-full text-xs text-muted-foreground hover:text-foreground">Not now</button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-views                                                          */
/* ------------------------------------------------------------------ */

type FundsView =
  | 'overview'
  | 'deposit_amount'    // SAB-490: single deposit screen (amount + method + summary)
  | 'withdrawal_amount' // SAB-490: single withdraw screen (amount + payout + summary)
  | 'buy_credits';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

// SAB item 1 (certifier): Soap card deposit + withdrawal are gated PER-ACCOUNT, not by
// a global flag. The authoritative gate is server-side in soap-create-checkout /
// soap-create-withdrawal; the client reads its own membership via soap_access_enabled()
// (fail-closed) only to decide whether to show the option. See soapEnabled below.

export default function NFLFundsSection({ hideDeposit = false }: { hideDeposit?: boolean } = {}) {
  const { user, session } = useAuth();
  const { currentLeague } = useNFLLeague();
  const { toast } = useToast();
  const navigate = useNavigate();

  // SAB-466 Locance geo-compliance gate. FAIL CLOSED at the money moments
  // (deposit + withdrawal), server-authoritative verdict. geoActionRef holds the
  // last-attempted action so the "enable location" screen can retry it. Gated by
  // GEO_ENABLED (VITE_GEO_ENABLED): OFF (prod, until SAB-488) short-circuits to
  // allowed and NEVER calls Locance — deposits behave exactly as before the gate.
  const geo = useLocanceGeoCheck();
  // Build 6 Phase A: PayPal/Venmo block shows on web OR native 1.2.0+.
  const { atLeast: nativeAtLeast } = useNativeAppVersion();
  const canUseRestoredPath = !Capacitor.isNativePlatform() || nativeAtLeast('1.2.0');
  const geoActionRef = useRef<(() => void) | null>(null);

  // Run the geo gate. Returns true ONLY on a Compliant verdict (FAIL CLOSED), OR
  // immediately when GEO_ENABLED is off (Locance never called → the money path
  // behaves exactly as before this gate existed). The WHY (Noncompliant →
  // out_of_region/vpn_proxy/device, or denied/transient/consent) is carried by
  // geo.geoBlock and rendered by <GeoBlockScreen/>; this returns only pass/fail.
  const passesGeoGate = async (retry: () => void): Promise<boolean> => {
    if (!GEO_ENABLED) return true;
    geoActionRef.current = retry;
    const verdict = await geo.run();
    return verdict?.result === 'Compliant';
  };

  // SAB-570: acquire a FRESH Locance session for a money ACTION (deposit/withdraw/
  // credits/card/paypal), to attach as geo_session_id so the SERVER re-verifies +
  // claims the same session. Re-gathers at money time (a page-load/pre-check session
  // can go stale). Returns { ok:false } on a failed/blocked gather — the caller aborts
  // and geo.geoBlock drives <GeoBlockScreen/> (friendly front door; the server gate is
  // the authoritative backstop). When GEO_ENABLED is off, returns ok with no session
  // (the server gate is off too) so the flow is unchanged.
  const acquireGeoSession = async (): Promise<{ ok: boolean; sessionId?: string }> => {
    // SAB-488: gather when enforcing (GEO_ENABLED) OR observing (GEO_OBSERVE) — the
    // server needs the session in both. Neither → no gather, flow unchanged.
    if (!GEO_ENABLED && !GEO_OBSERVE) return { ok: true };
    const verdict = await geo.run();
    // SAB item 5: geo.run() refreshed the cached GPS fix; device-ping if the last one is
    // stale (>30 min). Telemetry, non-blocking, never prompts. Covers deposit + withdrawal.
    pingBeforeMoneyActionIfStale(currentLeague?.id);
    // OBSERVE (enforce off): send the session so the server records the real verdict,
    // but NEVER fail-close — the money action proceeds regardless of the verdict.
    if (!GEO_ENABLED) return { ok: true, sessionId: verdict?.sessionId };
    if (verdict?.result === 'Compliant') return { ok: true, sessionId: verdict.sessionId };
    return { ok: false };
  };

  const [view, setView] = useState<FundsView>('overview');
  const [wallet, setWallet] = useState<PlayerWallet | null>(null);
  const [banks, setBanks] = useState<LinkedBank[]>([]);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  // Deposit state — persists across bank linking
  const [depositAmount, setDepositAmount] = useState('');
  const [payingByCard, setPayingByCard] = useState(false); // SAB-564 Soap card deposit
  // SAB-657: embedded Soap checkout URL (in-app iframe) + a bump key that (re)starts the
  // pending-row poll from either the iframe completion signal or a legacy full-reload return.
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [depositPollKey, setDepositPollKey] = useState(0);
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [depositing, setDepositing] = useState(false);
  // SAB-470 idempotency-key lifecycle: one stable key per deposit attempt.
  //  - new attempt / changed inputs → new key
  //  - retry after a "processing" (indeterminate) response → SAME key (this is
  //    the double-charge protection; the server returns the existing pending row)
  //  - retry after a definite failure, or after success → new key
  const depositIdemRef = useRef<{ key: string; amount: number; bankId: string; leagueId: string } | null>(null);

  // Credit purchase state
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  // SAB-493: profile-eligibility block (state/age — the DECLARED-state layer) →
  // feeds <GeoBlockScreen/>. Fail-OPEN by design (service down → allow); Locance
  // + KYC stay the hard gates. Re-checked each attempt so the profile_incomplete
  // → verify → retry loop closes. Independent of GEO_ENABLED (this layer already
  // runs on prod today).
  const [eligibilityBlock, setEligibilityBlock] = useState<GeoBlockReason | null>(null);

  // SAB-367 KYC gate state. Set when check_kyc_gate denies; null when allowed.
  // The `tier` field drives banner copy AND (Step 4) the Persona template
  // launched. Reasons: 'not_verified' | 'profile_not_found' | 'tier_too_low'
  // | 'error'.
  const [kycBlocked, setKycBlocked] = useState<{ tier: 1 | 2; reason: string } | null>(null);

  // True while the Persona SDK is loading, the inquiry is open, or the
  // post-complete server gate recheck is retrying. Disables the verify button.
  const [kycVerifying, setKycVerifying] = useState(false);

  // SAB-490 4b: real KYC status from profiles.kyc_status (webhook-maintained).
  // DISPLAY ONLY — drives the "Identity Verified" badge + result states. It
  // NEVER releases a deposit/withdrawal; check_kyc_gate (server) is the gate.
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  // Result overlay after a Persona inquiry: the poll window + its outcome.
  const [kycResult, setKycResult] = useState<null | 'verifying' | 'verified' | 'pending' | 'needs_review' | 'failed' | 'tier_required'>(null);
  const [kycFlowTier, setKycFlowTier] = useState<1 | 2>(1);

  // SAB-367 withdrawal state. Mirrors deposit; reuses selectedBankId for
  // the bank picker built in Step 3.
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  // Soap card payout selected. Ceiling withdrawal: the user picks the amount on
  // Soap's page, so our screen does not collect one when this is chosen.
  const [soapPayout, setSoapPayout] = useState(false);
  // SAB item 1: per-account Soap availability (UX gate only; the server enforces).
  // Starts false and flips true ONLY on an explicit soap_access_enabled()===true, so a
  // slow or failed check never flashes the card option to a non-enrolled user.
  const [soapEnabled, setSoapEnabled] = useState(false);
  useEffect(() => {
    if (!user) { setSoapEnabled(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (supabase as any).rpc('soap_access_enabled');
        if (!cancelled) setSoapEnabled(!error && data === true);
      } catch {
        if (!cancelled) setSoapEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Bank connect state
  const [connecting, setConnecting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Profile info for Aeropay
  const [phoneNumber, setPhoneNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Track whether we came from deposit flow (to return after bank link)
  const [returnToDeposit, setReturnToDeposit] = useState(false);

  const leagueId = currentLeague?.id;

  /* ------------------------------------------------------------------ */
  /*  SAB-490 stubs — CLEARLY placeholder. Do NOT treat as real state.   */
  /*  Wire each to its real source before prod (tracked in the ticket).  */
  /* ------------------------------------------------------------------ */
  const kycVerified = kycStatus === 'approved';          // SAB-490 4b: real KYC status (webhook-maintained), display only

  // SAB-491 (Dan, post-7/14) — the following were fabricated placeholders and are
  // now HIDDEN (not stubbed) so NO fake numbers render to users. Each is re-enabled
  // at its marked render spot when the real value ships:
  //   • referral-bonus promo rate  → deposit summary ("Referral bonus" line + toWallet)
  //   • weekly deposit-limit policy → deposit summary (limit meter)
  //   • bonus-cash / lifetime-net   → wallet hero sub-tiles (no wallet column/aggregate
  //     exists yet; wallet_transactions is fetched .limit(20), so it is NOT a lifetime
  //     source — needs a server aggregate + a product definition of "net"/"bonus cash")

  /* ---------------------------------------------------------------- */
  /*  Data fetching                                                    */
  /* ---------------------------------------------------------------- */

  const loadData = useCallback(async () => {
    if (!user || !leagueId) return;
    setLoading(true);

    const { data: prof } = await supabase
      .from('profiles')
      .select('phone_number, display_name, username, kyc_status' as any)
      .eq('id', user.id)
      .single();

    if (prof) {
      const p = prof as any;
      if (p.phone_number) setPhoneNumber(p.phone_number);
      setKycStatus(p.kyc_status ?? null);
      const name = p.display_name || p.username || '';
      const parts = name.trim().split(/\s+/);
      if (!firstName && parts[0]) setFirstName(parts[0]);
      if (!lastName && parts.length > 1) setLastName(parts.slice(1).join(' '));
    }

    // Real money (balance, pending_deposits) lives in the per-user wallet;
    // sabotage_credits stays per-league on player_wallets.
    const [{ data: uw }, { data: pw }] = await Promise.all([
      supabase.from('user_wallets').select('balance, pending_deposits').eq('user_id', user.id).maybeSingle(),
      supabase.from('player_wallets').select('id, sabotage_credits').eq('user_id', user.id).eq('league_id', leagueId).maybeSingle(),
    ]);
    const w = (uw || pw) ? {
      id: (pw as { id?: string } | null)?.id ?? null,
      balance: Number((uw as { balance?: number } | null)?.balance ?? 0),
      sabotage_credits: Number((pw as { sabotage_credits?: number } | null)?.sabotage_credits ?? 0),
      pending_deposits: Number((uw as { pending_deposits?: number } | null)?.pending_deposits ?? 0),
    } : null;

    setWallet(w as PlayerWallet | null);

    const { data: b } = await supabase
      .from('linked_bank_accounts')
      .select('id, bank_name, account_last_four, account_type, status')
      .eq('user_id', user.id)
      .eq('status', 'active');

    // Fix 3: sanitize bank labels at the data layer so every render site (method
    // rows, Manage, activity) shows a clean name — never "(oAuth)"-style qualifiers.
    setBanks((((b as any[]) || []).map((x) => ({ ...x, bank_name: cleanBankLabel(x.bank_name) }))) as LinkedBank[]);
    // SAB-490: no silent default bank. Deposit keeps its own `|| banks[0]`
    // fallbacks; Withdraw requires a deliberate payout-destination tap (money-out).

    const { data: t } = await supabase
      .from('wallet_transactions')
      .select('id, type, amount, status, created_at, completed_at, prize_pool_amount, rake_amount, description')
      .eq('user_id', user.id)
      .eq('league_id', leagueId)
      .order('created_at', { ascending: false })
      .limit(20);

    setTransactions((t as WalletTransaction[]) || []);
    setLoading(false);
  }, [user, leagueId]);

  useEffect(() => { loadData(); }, [loadData]);

  // SAB-979: a PayPal deposit and a Persona verification both complete SERVER-SIDE via
  // webhook while the user is in a system browser. Nothing tells this component, so the
  // balance and KYC state it holds go stale the moment the user leaves. Re-read on return.
  // This is not react-query, so ForegroundRefetch's invalidateQueries does not reach it.
  useAppForeground(loadData);

  // SAB-490 4b: auto-dismiss the brief "verified" success overlay once the flow
  // has advanced (the money view is already changing underneath it).
  useEffect(() => {
    if (kycResult !== 'verified') return;
    const t = setTimeout(() => setKycResult(null), 1400);
    return () => clearTimeout(t);
  }, [kycResult]);

  /* ---------------------------------------------------------------- */
  /*  Edge function caller                                             */
  /* ---------------------------------------------------------------- */

  const callEdgeFunction = async (name: string, body: any) => {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
      // supabase-js FunctionsHttpError: `error.message` is generic ("Edge Function
      // returned a non-2xx status code"). The real JSON body ({ error, code, ... })
      // is on `error.context` (a Response). Parse it so callers can surface the
      // ACTUAL failure reason (payout_failed, insufficient_balance, kyc_required,
      // rg block, …) instead of the transport string. Falls back to the generic
      // message only if there is no JSON body.
      let parsed: any = null;
      const ctx: any = (error as any)?.context;
      try {
        if (ctx?.clone) parsed = await ctx.clone().json();
        else if (typeof ctx?.json === 'function') parsed = await ctx.json();
      } catch { /* body not JSON — keep parsed = null */ }
      const e = new Error(parsed?.error || error.message || 'Request failed') as Error & { code?: string; body?: any };
      if (parsed?.code) e.code = parsed.code;
      if (parsed && typeof parsed === 'object') e.body = parsed;
      throw e;
    }
    if (data?.error) throw new Error(data.error);
    return data;
  };

  // SAB-605: one router for every deposit method on this surface (PayPal via
  // BrandedPayButton.onError, plus the Soap card and Aeropay ACH catches below).
  // mapDepositError turns the edge fn's code into human copy + an action, so a working
  // geo block reads as a location message (not "Payment Failed"), and a KYC block
  // re-gates to Persona instead of a dead toast.
  const routeMoneyError = (err: any) => {
    const { title, message, action } = mapDepositError(err);
    if (action === 'verify_identity') {
      setKycBlocked({ tier: 1, reason: 'not_verified' });
      setView('overview');
      toast({ title: 'Verification required', description: message, variant: 'destructive' });
      return;
    }
    if (action === 'responsible_gaming') {
      toast({ title, description: message, variant: 'destructive' });
      navigate('/responsible-gaming');
      return;
    }
    // geo / min-deposit / insufficient / generic → surface the mapped copy
    toast({ title, description: message, variant: 'destructive' });
  };

  /* ---------------------------------------------------------------- */
  /*  Connect bank handler                                             */
  /* ---------------------------------------------------------------- */

  const saveProfileInfo = async () => {
    if (!user) return;
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
      throw new Error('Please enter a valid 10-digit US phone number.');
    }
    const formattedPhone = cleanPhone.length === 11 ? cleanPhone : `1${cleanPhone}`;

    if (!firstName.trim()) throw new Error('First name is required.');
    if (!lastName.trim()) throw new Error('Last name is required.');

    const { error } = await supabase
      .from('profiles')
      .update({ phone_number: formattedPhone } as any)
      .eq('id', user.id);

    if (error) throw new Error('Failed to save phone number.');
    setPhoneNumber(formattedPhone);
  };



  /* ---------------------------------------------------------------- */
  /*  Deposit navigation                                               */
  /* ---------------------------------------------------------------- */

  const startDeposit = async () => {
    geoActionRef.current = startDeposit;
    // Profile-eligibility (state/age) — the DECLARED-state layer. Fail-OPEN by
    // design: a down eligibility service must NOT block (Locance + KYC are the
    // hard gates). Re-checked every attempt so the profile_incomplete → verify
    // → retry loop closes. Blocks funnel to <GeoBlockScreen/>.
    setEligibilityBlock(null);
    try {
      const res = await callEdgeFunction('check-eligibility', { action: 'get_user_status' });
      const block = eligibilityToGeoBlock(res);
      if (block) {
        setEligibilityBlock(block);
        return;
      }
    } catch {
      // fail open — proceed to the hard gates below
    }

    // SAB-367 tier-1 KYC gate. FAIL CLOSED on any error or non-allowed
    // response: a KYC gate that fails open is a compliance hole. This is
    // intentionally DIFFERENT from the check-eligibility branch above,
    // which fails open by design. Do NOT "fix" this catch to match the
    // eligibility convention.
    let kycAllowed = false;
    let kycReason = 'error';
    try {
      if (!user) throw new Error('no user');
      const { data: gate, error } = await (supabase as any).rpc('check_kyc_gate', {
        p_user_id: user.id,
        p_required_tier: 1,
      });
      if (!error && gate?.allowed === true) {
        kycAllowed = true;
      } else {
        kycReason = gate?.reason ?? 'error';
      }
    } catch {
      kycAllowed = false;
      kycReason = 'error';   // fail closed
    }

    if (!kycAllowed) {
      setKycBlocked({ tier: 1, reason: kycReason });
      toast({
        title: 'Verification required',
        description: 'Identity verification required before depositing',
        variant: 'destructive',
      });
      return;
    }
    setKycBlocked(null);

    // SAB-466 geo gate — ordering is eligibility → KYC → geo, run AFTER KYC so we
    // never prompt for location on a user who'd fail KYC anyway. FAIL CLOSED:
    // proceed only on a Compliant verdict (or immediately when GEO_ENABLED off).
    if (!(await passesGeoGate(startDeposit))) return;

    setDepositAmount('');
    setShowCustomAmount(false);
    setView('deposit_amount');
  };

  const handleDepositContinue = () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast({ title: 'Invalid amount', description: 'Enter a positive dollar amount.', variant: 'destructive' });
      return;
    }
    setView('deposit_amount');
  };


  /* ---------------------------------------------------------------- */
  /*  SAB-564 Card deposit (Soap Payments hosted checkout)             */
  /* ---------------------------------------------------------------- */

  const handlePayByCard = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) return;
    const amount_cents = Math.round(amount * 100);
    setPayingByCard(true);
    try {
      // SAB-570: fresh geo session for this card deposit.
      const geoSess = await acquireGeoSession();
      if (!geoSess.ok) { setPayingByCard(false); return; }
      const res = await callEdgeFunction('soap-create-checkout', {
        amount_cents,
        league_id: leagueId,
        // SAB-657: embed the checkout in-app (iframe) instead of navigating out.
        experience: 'iframe',
        // Soap redirects here INSIDE the frame after checkout; the webhook is the source of truth.
        return_url: `${window.location.origin}/nfl?soap_return=1`,
        ...(geoSess.sessionId ? { geo_session_id: geoSess.sessionId } : {}),
      });
      if (res?.url) {
        // SAB-657: render the embedded checkout instead of leaving the app.
        setCheckoutUrl(res.url);
      } else {
        toast({ title: 'Card checkout failed', description: 'Could not start the checkout. Please try again.', variant: 'destructive' });
      }
    } catch (err: any) {
      routeMoneyError(err);   // SAB-605: geo/KYC/limit/generic via the shared mapper
    } finally {
      setPayingByCard(false);
    }
  };

  // Return-landing from the Soap hosted checkout. Show a processing state driven
  // by the pending row status (poll) — never assume success; the webhook credits.
  // SAB-657: (re)start the pending-row poll — from the iframe completion signal or a legacy
  // full-reload return. Bumping depositPollKey drives the single poll effect below.
  const startDepositPoll = useCallback(() => {
    setCheckoutUrl(null);
    setDepositPollKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('soap_return') !== '1' || !user) return;
    // Clear the param so a refresh doesn't re-trigger.
    window.history.replaceState({}, '', window.location.pathname);
    startDepositPoll();
  }, [user, startDepositPoll]);

  // The single poll implementation (never assume success; the webhook is the truth).
  useEffect(() => {
    if (depositPollKey === 0 || !user) return;
    let cancelled = false;
    let tries = 0;
    toast({ title: 'Processing your card deposit', description: 'This can take a few moments; your balance updates when it settles.' });
    const poll = async () => {
      if (cancelled) return;
      tries++;
      const { data } = await supabase
        .from('soap_pending_deposits')
        .select('status, amount_cents')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const st = (data as any)?.status;
      if (st === 'succeeded') {
        await loadData();
        toast({ title: 'Deposit Successful', description: `$${(((data as any)?.amount_cents || 0) / 100).toFixed(2)} added to your wallet.` });
        return;
      }
      if (['failed', 'voided', 'returned', 'expired', 'terminally_failed'].includes(st)) {
        toast({ title: 'Deposit not completed', description: 'Your card deposit did not go through. You were not charged, or any charge will be reversed.', variant: 'destructive' });
        return;
      }
      // SAB-657: bounded give-up. Soap's in-widget cancel/X is indistinguishable from a
      // completion at the client, so an abandoned checkout lands here with the row still
      // 'created'. Give up with an HONEST message that covers both outcomes — never imply
      // money moved. The webhook stays authoritative (a real completion resolves earlier).
      if (tries >= 12) {
        toast({ title: "We couldn't confirm your deposit", description: "If you completed payment, your balance updates automatically once it settles. If you cancelled, you weren't charged." });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [depositPollKey, user]);

  // Return-landing from a Soap WITHDRAWAL. The debit happens server-side on
  // checkout.hold; the client just refetches to reflect the new balance.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('soap_wd_return') !== '1' || !user) return;
    window.history.replaceState({}, '', window.location.pathname);
    toast({ title: 'Updating your wallet', description: 'Your balance reflects any completed withdrawal.' });
    loadData();
  }, [user]);


  /* ---------------------------------------------------------------- */
  /*  SAB-367 Withdrawal navigation                                    */
  /* ---------------------------------------------------------------- */

  const startWithdrawal = async () => {
    geoActionRef.current = startWithdrawal;
    // No profile-eligibility (state/age) gate on withdrawal by design: money-OUT
    // must not be trapped if a user's declared state changes. KYC tier-2 + the
    // Locance geo gate remain. Clear any stale deposit-layer block.
    setEligibilityBlock(null);
    // SAB-367 tier-2 KYC gate. FAIL CLOSED on any error or non-allowed
    // response, same as the tier-1 deposit gate. A KYC gate that fails
    // open is a compliance hole. Do NOT "fix" this catch to match the
    // check-eligibility fail-open convention elsewhere in this file.
    let kycAllowed = false;
    let kycReason = 'error';
    try {
      if (!user) throw new Error('no user');
      const { data: gate, error } = await (supabase as any).rpc('check_kyc_gate', {
        p_user_id: user.id,
        p_required_tier: 2,
      });
      if (!error && gate?.allowed === true) {
        kycAllowed = true;
      } else {
        kycReason = gate?.reason ?? 'error';
      }
    } catch {
      kycAllowed = false;
      kycReason = 'error';   // fail closed
    }

    if (!kycAllowed) {
      setKycBlocked({ tier: 2, reason: kycReason });
      toast({
        title: 'Verification required',
        description: 'Identity verification required before withdrawing',
        variant: 'destructive',
      });
      return;
    }
    setKycBlocked(null);

    // SAB-466 geo gate — eligibility → KYC → geo, run AFTER KYC. FAIL CLOSED
    // (or immediately when GEO_ENABLED off).
    if (!(await passesGeoGate(startWithdrawal))) return;

    setWithdrawalAmount('');
    setSelectedBankId(null);   // SAB-490: deliberate payout-destination tap; no silent default
    setSoapPayout(false);
    setView('withdrawal_amount');
  };


  /* ---------------------------------------------------------------- */
  /*  Soap card withdrawal. No amount on our side: soap-create-withdrawal */
  /*  sends balance_amount_cents = withdrawable as a CEILING and the user  */
  /*  picks the amount on Soap's page; the debit is server-side on hold.   */
  /* ---------------------------------------------------------------- */
  const handleWithdrawSoap = async () => {
    setWithdrawing(true);
    try {
      // Fresh geo session for this money-out action (same path as deposit and join).
      const geoSess = await acquireGeoSession();
      if (!geoSess.ok) { setWithdrawing(false); return; }
      const res = await callEdgeFunction('soap-create-withdrawal', {
        experience: Capacitor.isNativePlatform() ? 'webview' : 'web',
        return_url: `${window.location.origin}${window.location.pathname}?soap_wd_return=1`,
        ...(geoSess.sessionId ? { geo_session_id: geoSess.sessionId } : {}),
      });
      if (res?.url) {
        window.location.href = res.url;   // redirect to hosted checkout; page unloads
      } else {
        toast({ title: 'Withdrawal failed', description: 'Could not start the card withdrawal. Please try again.', variant: 'destructive' });
        setWithdrawing(false);
      }
    } catch (err: any) {
      routeMoneyError(err);   // shared mapDepositError mapper (geo / KYC / RG / generic)
      setWithdrawing(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Purchase credits handler                                         */
  /* ---------------------------------------------------------------- */

  const handlePurchaseCredits = async () => {
    if (!selectedCredits) return;
    setPurchasing(true);
    try {
      // SAB-570: fresh geo session for this credit purchase.
      const geoSess = await acquireGeoSession();
      if (!geoSess.ok) { setPurchasing(false); return; }
      await callEdgeFunction('purchase-credits', {
        league_id: leagueId,
        credit_amount: selectedCredits,
        ...(geoSess.sessionId ? { geo_session_id: geoSess.sessionId } : {}),
      });

      const fee = selectedCredits * FEE_RATE;
      toast({
        title: 'Credits Purchased',
        description: `${selectedCredits} sabotage credits added. $${(selectedCredits + fee).toFixed(2)} charged.`,
      });

      setSelectedCredits(null);
      await loadData();
      setView('overview');
    } catch (err: any) {
      // SAB-552: purchase-credits is money-in (funds the prize pool) and is
      // KYC-gated server-side. Route a KYC_REQUIRED block to the tier-1 Persona
      // flow rather than a dead-end error toast.
      if (err?.code === 'KYC_REQUIRED') {
        setKycBlocked({ tier: 1, reason: 'not_verified' });
        setView('overview');
        toast({ title: 'Verification required', description: 'Verify your identity before buying credits.', variant: 'destructive' });
      } else {
        // SAB-662: PURCHASE_LIMIT / RG_BLOCKED / geo → mapped copy + RG-settings route, not a
        // generic "Purchase Failed". (KYC keeps its dedicated flow above.)
        routeMoneyError(err);
      }
    } finally {
      setPurchasing(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  SAB-367 KYC verification (Persona embedded)                      */
  /* ---------------------------------------------------------------- */

  // Server-authoritative recheck after the Persona inquiry completes.
  // CRITICAL: only advance the deposit/withdrawal on a server allowed===true
  // from check_kyc_gate. The SDK's status field is a UX hint only and must
  // NOT be trusted to gate money movement. The continuation (which view to
  // advance to) is passed in as onSuccess so this helper stays flow-agnostic.
  const recheckKycAndContinue = async (tier: 1 | 2, onSuccess: () => void) => {
    if (!user) return;
    setKycResult('verifying');           // visualize the async-webhook poll window
    const MAX_ATTEMPTS = 3;
    const DELAY_MS = 1500;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, DELAY_MS));
      try {
        const { data: gate, error } = await (supabase as any).rpc('check_kyc_gate', {
          p_user_id: user.id,
          p_required_tier: tier,
        });
        // SERVER-AUTHORITATIVE: money advances ONLY on the server gate. The Persona
        // SDK status is never trusted; kycStatus (below) is display-only and does
        // NOT release money.
        if (!error && gate?.allowed === true) {
          setKycStatus('approved');
          setKycBlocked(null);
          setKycVerifying(false);
          setKycResult('verified');      // brief success overlay; auto-dismissed by effect
          onSuccess();                   // advance the deposit/withdrawal flow
          return;
        }
      } catch {
        // swallow and retry; the webhook may not have landed yet
      }
    }
    // Gate still not allowed after the poll window. Read kyc_status to pick the
    // right result state — DISPLAY ONLY, this does NOT release money.
    setKycVerifying(false);
    let status: string | null = null;
    try {
      const { data } = await supabase.from('profiles').select('kyc_status').eq('id', user.id).maybeSingle();
      status = (data as any)?.kyc_status ?? null;
      setKycStatus(status);
    } catch { /* leave status null */ }
    // approved-but-gate-denied ⟹ insufficient tier (e.g. a tier-1-verified user
    // hitting the tier-2 withdrawal gate) — a distinct, accurate state, NOT the
    // generic "pending / under review".
    setKycResult(
      status === 'failed' ? 'failed'
        : status === 'needs_review' ? 'needs_review'
        : status === 'approved' ? 'tier_required'
        : 'pending'
    );
  };

  const startKycVerification = async (tier: 1 | 2) => {
    if (!user || kycVerifying) return;
    // SAB-626: tier 2 uses Persona's HOSTED redirect. The embedded iframe below is
    // refused by X-Frame-Options: sameorigin on inquiry.withpersona.com, so tier-2
    // KYC (the withdrawal gate) never opened. We leave the app to Persona and return
    // via /persona-return, which polls check_kyc_gate and lands the user back on the
    // wallet (returnPath). persona-webhook resolves tier 2 from the template id and
    // writes the tier-2 row + kyc_tier=2 on approve. The tier-1 embedded path below
    // is deliberately left as-is (out of scope for this fix).
    if (tier === 2) {
      setKycVerifying(true);
      try {
        await launchPersonaHosted({ tier: 2, userId: user.id, returnPath: window.location.pathname + window.location.search });
      } catch (err) {
        // buildPersonaHostedUrl throws on a malformed template/environment id, so a
        // bad config fails loud here instead of hanging the button at "Starting...".
        console.error('[persona] hosted launch failed:', err);
        setKycVerifying(false);
        toast({ title: 'Verification unavailable', description: 'Could not start verification. Please try again.', variant: 'destructive' });
      }
      return; // web: the page unloads to Persona; native: the external browser opens.
    }
    setKycFlowTier(tier);
    setKycResult(null);   // clear any prior result overlay (e.g. retry from 'failed')
    setKycVerifying(true);
    try {
      await loadPersonaSdk();
    } catch {
      setKycVerifying(false);
      toast({
        title: 'Verification unavailable',
        description: 'Could not load the verification client. Try again.',
        variant: 'destructive',
      });
      return;
    }

    const Persona = (window as any).Persona;
    if (!Persona?.Client) {
      setKycVerifying(false);
      toast({
        title: 'Verification unavailable',
        description: 'Verification client did not initialize. Try again.',
        variant: 'destructive',
      });
      return;
    }

    // Tier picks the template and the post-success continuation.
    // referenceId stays user.id for both tiers (same webhook contract).
    const templateId = tier === 2 ? PERSONA_TIER2_TEMPLATE_ID : PERSONA_TIER1_TEMPLATE_ID;
    const onSuccess = () => {
      if (tier === 2) {
        setWithdrawalAmount('');
        setSelectedBankId(null);   // SAB-490: deliberate payout-destination tap; no silent default
        setView('withdrawal_amount');
      } else {
        setDepositAmount('');
        setShowCustomAmount(false);
        setView('deposit_amount');
      }
    };

    const client = new Persona.Client({
      templateId,
      environmentId: PERSONA_ENVIRONMENT_ID,
      // Contract: the webhook keys verification updates off referenceId.
      // referenceId MUST equal user.id or the server gate will never flip.
      referenceId: user.id,
      onReady: () => client.open(),
      onComplete: async () => {
        // Persona finished in-app. The webhook updates kyc server-side
        // asynchronously, so re-check the gate (with retry) before
        // advancing. The SDK status is NOT trusted to gate money movement.
        await recheckKycAndContinue(tier, onSuccess);
      },
      onCancel: () => {
        // User backed out. Leave kycBlocked as-is so the banner persists.
        setKycVerifying(false);
      },
      onError: (err: any) => {
        console.error('[persona] inquiry error:', err);
        setKycVerifying(false);
        toast({
          title: 'Verification error',
          description: 'Something went wrong during verification. Try again.',
          variant: 'destructive',
        });
      },
    });
  };


  // SAB-490 4b: KYC result overlay (poll window + outcome). Rendered in every
  // money view so it shows during verify and briefly
  // after the flow advances.
  const kycResultEl = kycResult ? (
    <KycResultModal
      result={kycResult}
      tier={kycFlowTier}
      onRetry={() => startKycVerification(kycFlowTier)}
      onClose={() => setKycResult(null)}
    />
  ) : null;

  /* ---------------------------------------------------------------- */
  /*  Loading                                                          */
  /* ---------------------------------------------------------------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }


  /* ---------------------------------------------------------------- */
  /*  Deposit Step 1: Select Amount (2x2 grid + custom)                */
  /* ---------------------------------------------------------------- */

  if (view === 'deposit_amount') {
    const amount = parseFloat(depositAmount) || 0;
    const CHIPS = [25, 100, 250, 500];
    const kycRequired = kycBlocked?.tier === 1;
    // Geo is gated in startDeposit (you cannot reach this view unless passesGeoGate
    // returned Compliant, or GEO_ENABLED is off) — no geoStatus button clause here.
    const canDeposit = amount > 0 && !kycRequired;

    return (
      <div className="space-y-4">
        {kycResultEl}
        <button onClick={() => setView('overview')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to Wallet
        </button>
        <h2 className="text-xl font-display font-bold text-foreground">Deposit</h2>

        {/* KYC-required gate (tier 1) */}
        {kycRequired && (
          <KycGate tier={1} verifying={kycVerifying} onStart={() => startKycVerification(1)} />
        )}

        {/* Amount */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Amount</p>
          <div className="flex items-center gap-1 rounded-xl border border-primary/40 bg-muted px-4 h-16 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30">
            <span className="text-2xl font-display font-bold text-muted-foreground">$</span>
            <Input
              type="text" inputMode="decimal" placeholder="0"
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              className="border-0 bg-transparent h-full text-3xl font-display font-extrabold text-foreground placeholder:text-muted-foreground/50 tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0 px-1"
            />
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {CHIPS.map(c => (
              <button
                key={c}
                onClick={() => setDepositAmount(String(c))}
                className={cn(
                  'h-11 rounded-lg border text-sm font-semibold transition-colors',
                  Number(depositAmount) === c ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted text-foreground hover:border-primary/40'
                )}
              >
                ${c}
              </button>
            ))}
          </div>
        </div>


        {/* Summary. SAB-491 (post-7/14): the "Referral bonus" line and the weekly
            deposit-limit meter were REMOVED here — both rendered fabricated numbers
            (10% of the amount; a hardcoded $500 limit). Re-add each with Dan's real
            values. Until then the summary shows only the real deposit amount, which
            credits the wallet 1:1. */}
        <Card className="border-border bg-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Deposit</span>
              <span className="text-foreground tabular-nums">${fmtUSD(amount)}</span>
            </div>
            <div className="border-t border-border pt-3 flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">To your wallet</span>
              <span className="text-lg font-display font-bold text-foreground tabular-nums">${fmtUSD(amount)}</span>
            </div>
            <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
              Must be 18+ (19+ in Nebraska) and physically located in an eligible state. Deposits are final.
            </p>

            {/* SAB-564: debit / credit card via Soap hosted checkout — dark until go-live */}
            {soapEnabled && RAIL_SOAP && (
              <div>
                <Button
                  onClick={handlePayByCard}
                  disabled={!canDeposit || payingByCard || depositing}
                  variant="outline"
                  className="w-full h-12 font-semibold text-base border-border disabled:opacity-40"
                >
                  {payingByCard
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting checkout...</>
                    : <><CreditCard className="h-4 w-4 mr-2" /> Debit / credit card</>}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center leading-relaxed mt-2">
                  You'll finish on our secure card checkout, then return here. Your balance updates once the payment settles.
                </p>
                {/* Card-brand acceptance marks (Soap card-brand review) */}
                <div className="flex items-center justify-center gap-3 mt-3">
                  <PaymentMarks size="sm" />
                  <span className="text-[10px] text-muted-foreground">Payments processed by Soap</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Other methods — PayPal / Venmo (synchronous capture).
            SAB (native gate): hidden on native — the PayPal SDK's window.open crashes
            Capacitor's WKWebView. Web keeps it. Display gate only; reversed when the
            server-side order + @capacitor/browser fix lands.
            Rail-gated (depositRails). The `!soapEnabled` exclusion is GONE: every
            enabled rail renders together and the user picks. It existed so Soap-enrolled
            accounts saw a Soap-only world, which made PayPal unreachable for exactly the
            accounts able to transact. */}
        {canUseRestoredPath && RAIL_PAYPAL && (
        <div>
          <div className="flex items-center gap-3 px-1 mb-2">
            <div className="flex-1 border-t border-border" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">or pay another way</span>
            <div className="flex-1 border-t border-border" />
          </div>
            {/* Per-rail fee, recomputed on every amount change. PayPal and Venmo share
                PayPal's pricing, so one line covers both buttons below. The Soap card
                button above deliberately shows nothing -- there is no fee to pass on, and
                an explicit "$0.00" reads as a fee to go looking for. */}
            {(() => {
              const line = feeSentence('paypal', parseFloat(depositAmount));
              return line ? (
                <p className="px-1 mb-2 text-[10px] leading-snug text-muted-foreground">{line}</p>
              ) : null;
            })()}
          {window.paypal ? (
            <div className="grid grid-cols-2 gap-3">
              <BrandedPayButton
                fundingSource="paypal" amount={amount} leagueId={leagueId!} callEdgeFunction={callEdgeFunction}
                acquireGeoSession={acquireGeoSession}
                onSuccess={async () => { await loadData(); setDepositAmount(''); setView('overview'); toast({ title: 'Deposit Successful', description: `$${amount.toFixed(2)} added to your wallet.` }); }}
                onError={routeMoneyError}
                onPending={() => toast({ title: 'Confirming your deposit…', description: "This can take a few seconds. Please don't close this screen or pay again." })}
              />
              <BrandedPayButton
                fundingSource="venmo" amount={amount} leagueId={leagueId!} callEdgeFunction={callEdgeFunction}
                acquireGeoSession={acquireGeoSession}
                onSuccess={async () => { await loadData(); setDepositAmount(''); setView('overview'); toast({ title: 'Deposit Successful', description: `$${amount.toFixed(2)} added via Venmo.` }); }}
                onError={routeMoneyError}
                onPending={() => toast({ title: 'Confirming your deposit…', description: "This can take a few seconds. Please don't close this screen or pay again." })}
              />
            </div>
          ) : (
            <div className="text-center py-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Loading payment options...</p>
            </div>
          )}
        </div>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Deposit Step 2: Choose Payment Method                            */
  /* ---------------------------------------------------------------- */

  /* SAB-490: the old two-step deposit (`deposit_method`) was merged into the
     single `deposit_amount` screen above (amount + pay-with + summary on one
     view, per the 1b/1c handoff). SAB-1120: the ACH deposit branch went with the
     Aeropay retirement -- card and PayPal/Venmo are the deposit rails. */

  /* ---------------------------------------------------------------- */
  /*  SAB-367 Withdrawal Step 1: Pick Amount                           */
  /* ---------------------------------------------------------------- */

  if (view === 'withdrawal_amount') {
    const availableBalance = wallet?.balance || 0;
    const amount = parseFloat(withdrawalAmount) || 0;
    const WITHDRAW_MIN = 10;
    const CHIPS = [50, 100, 200];
    // ACH is no-fee, so "you'll receive" == amount. Instant-to-debit rail is NOT
    // built (SAB-490 decision: don't advertise a payout method with no backend),
    // so there is exactly one method and no fee row for a non-existent rail.
    const processingFee = 0;
    const youReceive = Math.max(0, amount - processingFee);
    // Explicit bank selection (no silent default). Min $10. NO client-side max
    // cap — Max fills to the balance but the backend RPC is the source of truth
    // for insufficient_balance.
    const canWithdraw = amount >= WITHDRAW_MIN && !!selectedBankId && !withdrawing;

    return (
      <div className="space-y-4">
        {kycResultEl}
        {/* SAB-614: relabel to "Back". Wallet.tsx already renders the outer "Back to
            Wallet"; this inner control returns to the funds overview, not the wallet. */}
        <button onClick={() => setView('overview')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <h2 className="text-xl font-display font-bold text-foreground">Withdraw</h2>

        {/* Amount. Hidden for Soap: it sends the withdrawable as a CEILING and the
            user picks the actual amount on Soap's page, so we collect none here. */}
        {!soapPayout && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Amount</p>
            <span className="text-[10px] text-muted-foreground tabular-nums">${fmtUSD(availableBalance)} available</span>
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-primary/40 bg-muted px-4 h-16 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30">
            <span className="text-2xl font-display font-bold text-muted-foreground">$</span>
            <Input
              type="text" inputMode="decimal" placeholder="0"
              value={withdrawalAmount}
              onChange={e => setWithdrawalAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              className="border-0 bg-transparent h-full text-3xl font-display font-extrabold text-foreground placeholder:text-muted-foreground/50 tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0 px-1"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {CHIPS.map(c => (
              <button
                key={c}
                onClick={() => setWithdrawalAmount(String(c))}
                className={cn('h-11 rounded-lg border text-sm font-semibold transition-colors',
                  Number(withdrawalAmount) === c ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted text-foreground hover:border-primary/40')}
              >
                ${c}
              </button>
            ))}
            <button
              onClick={() => setWithdrawalAmount(availableBalance.toFixed(2))}
              className={cn('h-11 rounded-lg border text-sm font-semibold transition-colors',
                Number(withdrawalAmount) === Number(availableBalance.toFixed(2)) && availableBalance > 0 ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted text-foreground hover:border-primary/40')}
            >
              Max
            </button>
          </div>
        </div>
        )}

        {/* Withdraw to. Soap card payout and/or Bank ACH; explicit selection, no silent
            default. Soap is offered even with no linked bank (card is its own destination). */}
        <div>
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Withdraw to</p>
            {/* SAB-722: required inline pointer to the withdrawal disclosure. */}
            <Link
              to="/withdrawals"
              className="text-[10px] text-primary underline underline-offset-2 hover:text-primary/80 shrink-0"
            >
              How withdrawals work
            </Link>
          </div>
          <div className="space-y-2">
            {soapEnabled && (
              <button
                type="button"
                onClick={() => { setSoapPayout(true); setSelectedBankId(null); }}
                className={cn('w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  soapPayout ? 'border-primary/60 ring-1 ring-primary/20 bg-primary/5' : 'border-border hover:border-primary/40')}
              >
                <span className={cn('h-4 w-4 rounded-full border flex items-center justify-center shrink-0', soapPayout ? 'border-primary' : 'border-muted-foreground')}>
                  {soapPayout && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                <CreditCard className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">Visa or Mastercard debit card</p>
                  {/* SAB-722: was "Instant." flat, which was a live prod claim Soap's
                      verified answer does not support: seconds-to-minutes where the card
                      or bank supports it, up to one business day on the same-day fallback.
                      Payouts are Visa/Mastercard only — Amex is deposit-only. */}
                  <p className="text-[10px] text-muted-foreground">
                    Usually within minutes; up to 1 business day. You choose the amount on
                    the next screen.
                  </p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Live summary + CTA. Soap carries no amount on our side (chosen on Soap's page),
            so it gets a ceiling note and its own action instead of the fee breakdown. */}
        {soapPayout ? (
          <Card className="border-border bg-card">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                You choose how much to withdraw on the secure card checkout, up to your available balance of <span className="text-foreground font-medium tabular-nums">${fmtUSD(availableBalance)}</span>.
              </p>
              <Button
                onClick={handleWithdrawSoap}
                disabled={availableBalance <= 0 || withdrawing}
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base disabled:opacity-40"
              >
                {withdrawing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting checkout...</> : <><CreditCard className="h-4 w-4 mr-2" /> Continue to card withdrawal</>}
              </Button>
              {kycVerified && (
                <p className="flex items-center justify-center gap-1.5 text-xs text-success"><ShieldCheck className="h-3.5 w-3.5" /> Identity verified</p>
              )}
              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                You finish on our secure card checkout, then return here. Your balance updates once it settles.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {/* Trust footer (4a) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> 256-bit encryption</span>
          <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Funds held by Sabotage Payments, LLC</span>
          <span className="ml-auto text-right"><span className="text-primary">Responsible Play</span> · 1-800-GAMBLER</span>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  SAB-367 Withdrawal Step 2: Method + Execute                      */
  /* ---------------------------------------------------------------- */

  /* SAB-490: the old two-step withdraw (`withdrawal_method`) was merged into the
     single `withdrawal_amount` screen above (amount + payout method + live
     summary on one view, per the 4a handoff). SAB-1120: the bank/ACH payout went
     with the Aeropay retirement; Soap card payout is the only rail, and it gates
     server-side in soap-create-withdrawal (SOAP_NOT_ENABLED -> RG -> KYC tier 2
     -> geo) rather than on a selected bank. */

  /* ---------------------------------------------------------------- */
  /*  Buy Credits View                                                 */
  /* ---------------------------------------------------------------- */

  if (view === 'buy_credits') {
    const balance = wallet?.balance || 0;

    return (
      <div className="space-y-4">
        <button onClick={() => setView('overview')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>

        <h2 className="text-base font-display font-bold text-foreground">Buy Sabotage Credits</h2>

        <Card className="border-[#2a3040]" style={{ backgroundColor: '#1E2536' }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-muted-foreground">Wallet Balance</span>
              <span className="text-sm font-bold text-emerald-400">${balance.toFixed(2)}</span>
            </div>

            <div className="space-y-2">
              {CREDIT_AMOUNTS.map(credits => {
                const fee = credits * FEE_RATE;
                const total = credits + fee;
                const canAfford = balance >= total;

                return (
                  <button
                    key={credits}
                    onClick={() => canAfford ? setSelectedCredits(credits) : null}
                    className={cn(
                      'w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left',
                      !canAfford && 'opacity-40 cursor-not-allowed',
                      selectedCredits === credits
                        ? 'border-orange-500 bg-orange-500/5'
                        : canAfford ? 'border-[#2a3040] hover:border-[#3a4050]' : 'border-[#2a3040]'
                    )}
                    style={{ backgroundColor: selectedCredits === credits ? undefined : '#181D27' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-damage/10 border border-damage/20 flex items-center justify-center">
                        <Zap className="h-4 w-4 text-damage" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{credits} Credits</p>
                        <p className="text-[10px] text-muted-foreground">${credits.toFixed(2)} + ${fee.toFixed(2)} fee</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={cn('text-sm font-bold tabular-nums', canAfford ? 'text-foreground' : 'text-muted-foreground')}>
                        ${total.toFixed(2)}
                      </span>
                      {!canAfford && <p className="text-[9px] text-red-400">Insufficient</p>}
                    </div>
                  </button>
                );
              })}
            </div>

            {balance < (CREDIT_AMOUNTS[0] + CREDIT_AMOUNTS[0] * FEE_RATE) && (
              <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400 text-center">
                  Insufficient balance.{' '}
                  <button onClick={startDeposit} className="underline font-medium hover:text-amber-300 transition-colors">
                    Deposit funds first
                  </button>
                </p>
              </div>
            )}

            {selectedCredits && (
              <div className="mt-4 p-3 rounded-xl border border-[#2a3040]" style={{ backgroundColor: '#181D27' }}>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Credits</span><span>${selectedCredits.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Platform fee (20%)</span><span>${(selectedCredits * FEE_RATE).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>To prize pool</span><span className="text-emerald-400">${selectedCredits.toFixed(2)}</span>
                </div>
                <div className="border-t border-[#2a3040] mt-2 pt-2 flex justify-between text-sm font-medium text-foreground">
                  <span>Total</span><span>${(selectedCredits + selectedCredits * FEE_RATE).toFixed(2)}</span>
                </div>
              </div>
            )}

            <Button
              onClick={handlePurchaseCredits}
              disabled={purchasing || !selectedCredits}
              className="w-full mt-4 h-11 bg-damage text-foreground hover:bg-damage/90 font-semibold"
            >
              {purchasing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><Zap className="h-4 w-4 mr-2" /> Buy {selectedCredits || '-'} Credits</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Overview (default)                                                */
  /* ---------------------------------------------------------------- */

  const statusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />;
      case 'failed': return <XCircle className="h-3.5 w-3.5 text-red-400" />;
      case 'pending': return <Clock className="h-3.5 w-3.5 text-amber-400" />;
      default: return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  const formatMethod = (desc: string | null) => {
    if (!desc) return null;
    const d = desc.toLowerCase();
    // Match by substring so descriptions like "Venmo deposit" / "ACH/RTP payout"
    // resolve to a clean method name and never double the type word in txnLabel.
    if (d.includes('paypal')) return 'PayPal';
    if (d.includes('venmo')) return 'Venmo';
    if (d.includes('ach') || d.includes('bank') || d.includes('rtp')) return 'ACH';
    // Fallback: strip a trailing "deposit"/"withdrawal" so "X deposit" -> "X".
    return desc.replace(/\s*(deposit|withdrawal)s?\s*$/i, '').trim() || null;
  };

  const txnLabel = (txn: WalletTransaction) => {
    const method = formatMethod(txn.description);
    switch (txn.type) {
      case 'deposit': return method ? `Deposit via ${method}` : 'Deposit';
      case 'sabotage_purchase': return 'Credit Purchase';
      case 'prize_payout': return 'Prize Payout';
      case 'withdrawal': return method ? `Withdrawal via ${method}` : 'Withdrawal';
      default: return txn.type;
    }
  };

  const methodColor = (desc: string | null) => {
    if (!desc) return '';
    const d = desc.toLowerCase();
    if (d.includes('paypal')) return 'text-blue-400';
    if (d.includes('venmo')) return 'text-[#3D95CE]';
    if (d.includes('ach') || d.includes('bank') || d.includes('rtp')) return 'text-orange-400';
    return '';
  };

  return (
    <div className="space-y-4">
      {/* SAB-657: embedded Soap card checkout — full-screen in-app iframe, never leaves the app. */}
      {checkoutUrl && (
        <SoapCheckoutFrame
          url={checkoutUrl}
          onComplete={startDepositPoll}
          onCancel={() => setCheckoutUrl(null)}
        />
      )}
      {/* SAB-493 4c geo-block screen — profile-eligibility layer (declared state)
          OR Locance layer (geo.geoBlock, detected state). Display-only; the money
          gates (KYC/RG/Locance verdict) stay server-authoritative. GEO_ENABLED off
          → the Locance layer never surfaces (geo.geoBlock stays null; run() is
          never called). */}
      {(eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)) && (
        <GeoBlockScreen
          reason={eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => geoActionRef.current?.()}
          onDismiss={() => {
            setEligibilityBlock(null);
            geo.reset();
          }}
        />
      )}

      {/* Title row + identity badge (1a) */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground tracking-tight">Wallet</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Manage deposits, withdrawals, and your withdrawable cash balance.</p>
        </div>
        {kycVerified && (
          <Badge className="gap-1 bg-success/10 text-success border-success/20 text-[10px] font-semibold shrink-0">
            <ShieldCheck className="h-3 w-3" /> Identity Verified
          </Badge>
        )}
      </div>

      {/* Balance hero — money is USD ($), never "SP" (SAB-490 B2) */}
      <Card className="border-primary/20 overflow-hidden" style={{ background: 'linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)' }}>
        <CardContent className="p-5">
          <div className="flex flex-col min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between gap-4 mb-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Withdrawable balance</p>
              <p className="text-4xl font-display font-extrabold text-foreground tabular-nums mt-1">
                ${(wallet?.balance || 0).toFixed(2)}
              </p>
            </div>
            <div className="flex gap-2 shrink-0 w-full min-[420px]:w-auto">
              {/* SAB-566 Fix 4: when embedded under the designed /wallet home, that
                  home owns the single Deposit CTA; hide this duplicate. */}
              {!hideDeposit && (
                <Button size="sm" onClick={startDeposit} className="h-9 flex-1 min-[420px]:flex-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                  <Plus className="h-4 w-4 mr-1.5" /> Deposit
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={startWithdrawal} className="h-9 flex-1 min-[420px]:flex-none border-border text-foreground hover:bg-muted font-semibold">
                <ArrowDown className="h-4 w-4 mr-1.5" /> Withdraw
              </Button>
            </div>
          </div>

          {/* Sub-tile: Pending (real). SAB-491 (post-7/14): the "Bonus cash" and
              "Lifetime net" tiles were REMOVED — both rendered a hardcoded $0.00
              (no bonus-cash balance or lifetime aggregate exists in the wallet data
              yet). Re-add here once a real server aggregate + product definition ship. */}
          <div className="grid grid-cols-1 gap-3">
            <div className="p-2.5 rounded-lg border border-border bg-background/60">
              <p className="text-[10px] text-muted-foreground">Pending</p>
              <p className="text-sm font-bold text-gold tabular-nums">${(wallet?.pending_deposits || 0).toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency clarity strip — intent preserved, labels corrected (SAB-490 B2):
          real cashable money is the USD Wallet Balance; SP + FAAB are in-game, no cash value. */}
      <div className="flex items-start gap-2.5 p-3 rounded-lg border border-primary/20" style={{ backgroundColor: 'hsl(25 95% 53% / 0.06)' }}>
        <Zap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Your <span className="text-foreground font-medium">Wallet Balance ($)</span> is real money. Deposit it, put it toward entries and the prize pool, and cash it out anytime.
          {' '}<span className="text-foreground font-medium">FAAB</span> (free-agent bidding budget) and <span className="text-foreground font-medium">Sabotage Points&nbsp;(SP)</span> (your in-game sabotage currency) are in-game only and have <span className="text-foreground font-medium">no cash value</span>.
        </p>
      </div>

      {/* SAB-367 KYC verify banner. Tier-aware copy AND tier-aware launch:
          the button passes kycBlocked.tier to startKycVerification, which
          selects the right Persona template (tier-1 deposit DB check vs
          tier-2 withdrawal ID+selfie) and the matching post-success
          continuation. referenceId is user.id for both, per webhook contract. */}
      {kycBlocked && (
        <KycGate tier={kycBlocked.tier} verifying={kycVerifying} onStart={() => startKycVerification(kycBlocked.tier)} />
      )}
      {kycResultEl}

      {/* SAB-490: "Buy Sabotage Credits" intentionally NOT on the wallet hub — the
          wallet is money-only ($). Credits are an in-game currency; the purchase
          entry lives on the Sabotages screen. Naming disambiguation tracked in SAB-492. */}


      {/* Recent activity (1a) */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium text-foreground">Recent activity</CardTitle>
          {transactions.length > 0 && (
            <span className="text-xs text-muted-foreground">Last {transactions.length}</span>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {transactions.length === 0 ? (
            <div className="py-8 text-center">
              <div className="h-12 w-12 rounded-full bg-muted border border-border flex items-center justify-center mx-auto mb-2.5">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-foreground font-medium">No activity yet</p>
              <p className="text-xs text-muted-foreground mt-0.5">Your deposits, winnings, and withdrawals will show up here.</p>
            </div>
          ) : (
            transactions.map(txn => (
              <div key={txn.id} className="flex items-center justify-between py-2.5 border-b border-border last:border-b-0">
                <div className="flex items-center gap-2.5">
                  {statusIcon(txn.status)}
                  <div>
                    <p className="text-sm text-foreground">{txnLabel(txn)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(txn.created_at).toLocaleDateString()}
                      {txn.description && (txn.type === 'deposit' || txn.type === 'withdrawal') && (
                        <span className={cn('ml-1.5 font-medium', methodColor(txn.description))}>
                          {formatMethod(txn.description)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn(
                    'text-sm font-medium tabular-nums',
                    txn.type === 'deposit' || txn.type === 'prize_payout' ? 'text-success' : 'text-foreground'
                  )}>
                    {txn.type === 'deposit' || txn.type === 'prize_payout' ? '+' : '-'}${txn.amount.toFixed(2)}
                  </p>
                  <Badge className={cn(
                    'text-[8px] px-1 py-0 border uppercase',
                    txn.status === 'completed' ? 'bg-success/10 text-success border-success/20'
                      : txn.status === 'failed' ? 'bg-damage/10 text-damage border-damage/20'
                      : 'bg-gold/10 text-gold border-gold/20'
                  )}>
                    {txn.status}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* SAB-288 Phase 6 — bonus earnings + YTD counter for the current
          user. Reads bonus_awards directly via RLS. KYC threshold warning
          gated by FEATURES.bonus_kyc_warning (off until SAB-176 ships). */}
      {user?.id && (
        <WalletBonusesSection
          userId={user.id}
          leagueId={currentLeague?.id}
        />
      )}

      {/* Trust footer (1a) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> 256-bit encryption</span>
        <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Funds held by Sabotage Payments, LLC</span>
        <span>Licensed &amp; regulated</span>
        <span className="ml-auto text-right">Must be 18+ (19+ in NE) &amp; in an eligible state · <span className="text-primary">Responsible Play</span></span>
      </div>
    </div>
  );
}
